import { SpeedIcon, BatteryIcon } from '@/pages/Business/pages/DriverMonitoring/components';
import { ClockIcon } from '@/pages/Business/pages/DriverMonitoring/components/ClockIcon';
import { translateStatus } from '@/pages/Business/pages/DriverMonitoring/components/DriverPopup.utils';
import { formatRelativeTime } from '@/utils';
import { DriverGpsPosition } from '../models/DriverGpsPosition';

interface DriverGpsInfoPanelProps {
  driverName?: string;
  position: DriverGpsPosition | undefined;
  isLoading: boolean;
  isStale: boolean;
  hasSignal: boolean;
  error: unknown;
}

export const DriverGpsInfoPanel = ({
  driverName,
  position,
  isLoading,
  isStale,
  hasSignal,
  error,
}: DriverGpsInfoPanelProps) => {
  const lastUpdate = position?.updatedAt || position?.createdAt;

  const renderStatusLine = () => {
    if (error) {
      return (
        <p className='text-sm text-red-600'>
          No se pudo obtener la ubicación del conductor
        </p>
      );
    }
    if (isLoading && !position) {
      return <p className='text-sm text-gray-500'>Buscando ubicación...</p>;
    }
    if (!position) {
      return (
        <p className='text-sm text-amber-600'>
          El conductor no tiene señal GPS activa
        </p>
      );
    }
    if (isStale) {
      return (
        <p className='text-sm text-amber-600'>
          {lastUpdate
            ? `Señal desactualizada — última ubicación hace ${formatRelativeTime(lastUpdate)}`
            : 'Señal GPS desactualizada'}
        </p>
      );
    }
    return null;
  };

  return (
    <div className='absolute top-3 left-3 bg-white px-4 py-2.5 rounded-lg shadow-lg border border-gray-200 z-[1000] max-w-xs'>
      <p className='text-base font-bold text-gray-900 leading-tight mb-1.5'>
        {driverName || 'Conductor'}
      </p>

      {renderStatusLine()}

      {hasSignal && position && (
        <>
          {position.status && (
            <div className='flex items-center gap-1.5 mt-1'>
              <div className='w-2 h-2 bg-green-500 rounded-full' />
              <p className='text-xs font-medium text-gray-600'>
                {translateStatus(position.status)}
              </p>
            </div>
          )}

          {position.speed !== undefined && (
            <div className='flex items-center gap-1.5 mt-1.5'>
              <SpeedIcon width={14} height={14} className='text-blue-500' />
              <p className='text-sm text-gray-700 font-medium'>
                {position.speed.toFixed(1)} km/h
              </p>
            </div>
          )}

          {position.batteryLevel !== undefined && (
            <div className='flex items-center gap-1.5 mt-1.5'>
              <BatteryIcon
                width={14}
                height={14}
                level={position.batteryLevel}
              />
              <p className='text-sm text-gray-700 font-medium'>
                {(position.batteryLevel * 100).toFixed(0)}%
              </p>
            </div>
          )}

          {lastUpdate && (
            <div className='flex items-center gap-1.5 mt-1'>
              <ClockIcon width={12} height={12} className='text-gray-500' />
              <p className='text-xs text-gray-500'>
                {formatRelativeTime(lastUpdate)}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
